import React from 'react';
import { Box, Paper, Typography, Chip, Stack } from '@mui/material';
import { TrendingUp, TrendingDown, AccountBalance } from '@mui/icons-material';
import { Portfolio } from '../../types';
import { formatCurrency } from '../../utils/formatters';

interface PortfolioMetrics {
  totalValue: number;
  totalGainLoss: number;
  totalGainLossPercent: number;
  positionCount: number;
  availableFunds: number;
  pendingCount: number;
}

interface MobilePortfolioSummaryProps {
  portfolio: Portfolio;
  metrics: PortfolioMetrics;
  onSelect?: () => void;
  selected?: boolean;
}

const MobilePortfolioSummary: React.FC<MobilePortfolioSummaryProps> = ({
  portfolio,
  metrics,
  onSelect,
  selected = false,
}) => {
  const isPositive = metrics.totalGainLoss >= 0;

  return (
    <Paper
      elevation={selected ? 3 : 1}
      sx={{
        p: 1.5,
        mb: 1.5,
        cursor: onSelect ? 'pointer' : 'default',
        border: selected ? '2px solid' : '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease',
        overflow: 'hidden',
        '&:active': onSelect ? { transform: 'scale(0.98)' } : {},
      }}
      onClick={onSelect}
    >
      {/* Portfolio Name & Type */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
          <AccountBalance sx={{ fontSize: 18 }} color="primary" />
          <Typography variant="subtitle2" fontWeight="bold" noWrap>
            {portfolio.name}
          </Typography>
          {portfolio.autopilotEnabled && (
            <Chip label="Auto" size="small" color="info" variant="filled" sx={{ height: 18, fontSize: '0.65rem' }} />
          )}
        </Stack>
      </Box>

      {/* Main Metrics - 2x2 Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
        {/* Unrealized P/L */}
        <Box sx={{ bgcolor: 'background.default', p: 0.75, borderRadius: 1, overflow: 'hidden' }}>
          <Typography variant="caption" color="textSecondary" display="block" fontSize="0.65rem">
            Unrealized P/L
          </Typography>
          <Stack direction="row" alignItems="center" spacing={0.25}>
            {isPositive ? (
              <TrendingUp sx={{ fontSize: 14 }} color="success" />
            ) : (
              <TrendingDown sx={{ fontSize: 14 }} color="error" />
            )}
            <Typography
              variant="body2"
              fontWeight="bold"
              color={isPositive ? 'success.main' : 'error.main'}
              noWrap
            >
              {formatCurrency(metrics.totalGainLoss)}
            </Typography>
          </Stack>
          <Typography
            variant="caption"
            fontSize="0.65rem"
            color={isPositive ? 'success.main' : 'error.main'}
          >
            {isPositive ? '+' : ''}{metrics.totalGainLossPercent.toFixed(2)}%
          </Typography>
        </Box>

        {/* Available Funds */}
        <Box sx={{ bgcolor: 'background.default', p: 0.75, borderRadius: 1, overflow: 'hidden' }}>
          <Typography variant="caption" color="textSecondary" display="block" fontSize="0.65rem">
            Available
          </Typography>
          <Typography variant="body2" fontWeight="bold" noWrap>
            {formatCurrency(metrics.availableFunds)}
          </Typography>
          <Typography variant="caption" color="textSecondary" fontSize="0.65rem">
            of {formatCurrency(portfolio.initialFundValue)}
          </Typography>
        </Box>

        {/* Earnings */}
        <Box sx={{ bgcolor: 'background.default', p: 0.75, borderRadius: 1, overflow: 'hidden' }}>
          <Typography variant="caption" color="textSecondary" display="block" fontSize="0.65rem">
            Earnings
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            color={(portfolio.totalEarnings || 0) >= 0 ? 'success.main' : 'error.main'}
            noWrap
          >
            {formatCurrency(portfolio.totalEarnings || 0)}
            {portfolio.initialFundValue > 0 && (
              <Typography component="span" variant="caption" sx={{ ml: 0.5 }}>
                ({(((portfolio.totalEarnings || 0) / portfolio.initialFundValue) * 100).toFixed(1)}%)
              </Typography>
            )}
          </Typography>
        </Box>

        {/* Positions */}
        <Box sx={{ bgcolor: 'background.default', p: 0.75, borderRadius: 1, overflow: 'hidden' }}>
          <Typography variant="caption" color="textSecondary" display="block" fontSize="0.65rem">
            Positions
          </Typography>
          <Typography variant="body2" fontWeight="bold">
            {metrics.positionCount} / {portfolio.maxPositions}
          </Typography>
          {metrics.pendingCount > 0 && (
            <Typography variant="caption" color="info.main" fontSize="0.65rem">
              +{metrics.pendingCount} pending
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default MobilePortfolioSummary;
